/**
 * Exit Intent Popup Component
 * Captures visitors who are about to leave the page
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, ArrowRight, Mail, Phone, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const EXIT_POPUP_KEY = 'caf_exit_popup_shown';
const POPUP_COOLDOWN_DAYS = 7;

const ExitIntentPopup = ({ 
  primaryColor = '#456CAC',
  onSubmit 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    // Check if popup was recently shown
    const lastShown = localStorage.getItem(EXIT_POPUP_KEY);
    if (lastShown) {
      const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      if (daysSince < POPUP_COOLDOWN_DAYS) {
        return; // Don't show again within cooldown period
      }
    }

    // Desktop: Mouse leave detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasTriggered) {
        setHasTriggered(true);
        setTimeout(() => setIsVisible(true), 100);
      }
    };

    // Mobile: Scroll up quickly or back button behavior
    let lastScrollY = window.scrollY;
    let scrollVelocity = 0;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollVelocity = lastScrollY - currentScrollY;
      
      // If scrolling up fast from middle of page
      if (scrollVelocity > 50 && currentScrollY < 200 && lastScrollY > 400 && !hasTriggered) {
        setHasTriggered(true);
        setTimeout(() => setIsVisible(true), 500);
      }
      
      lastScrollY = currentScrollY;
    };

    // Only add listeners after a delay (don't trigger immediately)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 5000); // Wait 5 seconds before enabling

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasTriggered]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem(EXIT_POPUP_KEY, Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email && !phone) return;
    
    // Call the onSubmit callback if provided
    if (onSubmit) {
      await onSubmit({ email, phone, source: 'exit_popup' });
    }
    
    setSubmitted(true);
    localStorage.setItem(EXIT_POPUP_KEY, Date.now().toString());
    
    // Close after showing success
    setTimeout(() => {
      setIsVisible(false);
    }, 3000);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9998]"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed z-[9999] w-[90%] max-w-md"
            style={{ 
              position: 'fixed',
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div 
              className="relative rounded-2xl overflow-hidden shadow-2xl bg-white"
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>

              {/* Header with gradient */}
              <div 
                className="pt-8 pb-6 px-8 text-center relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
              >
                {/* Decorative circles */}
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white/10 translate-y-1/2 -translate-x-1/2" />
                
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="relative"
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    Wait! Don't Leave Empty-Handed
                  </h2>
                  <p className="text-white/80 text-sm sm:text-base">
                    Get a FREE quote + exclusive deals delivered to your inbox
                  </p>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-8">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone (Optional)
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="(403) 555-1234"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full py-6 rounded-xl text-lg font-semibold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Get My Free Quote
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>

                    <p className="text-xs text-center text-gray-500">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-4"
                  >
                    <div 
                      className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring" }}
                      >
                        <Sparkles className="w-8 h-8" style={{ color: primaryColor }} />
                      </motion.div>
                    </div>
                    <h3 className="text-xl font-bold mb-2">You're All Set!</h3>
                    <p className="text-gray-600">
                      We'll be in touch within 24 hours with your personalized quote.
                    </p>
                  </motion.div>
                )}

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-sm">🔒</span>
                    </div>
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-sm">⚡</span>
                    </div>
                    <span>24hr Response</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-sm">🏆</span>
                    </div>
                    <span>10+ Years</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
